/**
 * Questionnaires tab for supplier detail page.
 * Shows assigned questionnaires (SupplierQualifications), status, scores, and renewal tracking.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  FileText, CheckCircle2, Clock, AlertTriangle, Eye, Loader2, Plus,
} from 'lucide-react';
import { intelligenceAPI, type SupplierQualification } from '../../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { AssignQuestionnaireDialog } from './AssignQuestionnaireDialog';

interface QuestionnairesTabProps {
  supplierId: number;
  supplierName?: string;
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  invited: { label: 'Invited', className: 'bg-gray-100 text-gray-800' },
  in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-800' },
  submitted: { label: 'Submitted', className: 'bg-cyan-100 text-cyan-800' },
  under_review: { label: 'Under Review', className: 'bg-purple-100 text-purple-800' },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
  more_info_required: { label: 'More Info Required', className: 'bg-amber-100 text-amber-800' },
  expired: { label: 'Expired', className: 'bg-red-100 text-red-800' },
};

export function QuestionnairesTab({ supplierId, supplierName }: QuestionnairesTabProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [questionnaires, setQuestionnaires] = useState<SupplierQualification[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const loadQuestionnaires = useCallback(async () => {
    setLoading(true);
    try {
      const res = await intelligenceAPI.getQualifications({ supplier: supplierId, page_size: 100 });
      const data = res.data.results || [];
      setQuestionnaires(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      console.error('Failed to load questionnaires:', err);
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    loadQuestionnaires();
  }, [loadQuestionnaires]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const total = questionnaires.length;
  const approved = questionnaires.filter(q => q.status === 'approved').length;
  const pending = questionnaires.filter(q => ['submitted', 'under_review'].includes(q.status)).length;
  const invited = questionnaires.filter(q => q.status === 'invited').length;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="text-2xl font-bold">{total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Approved</p>
            <p className="text-2xl font-bold text-green-600">{approved}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Pending Review</p>
            <p className="text-2xl font-bold text-blue-600">{pending}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Invited</p>
            <p className="text-2xl font-bold text-gray-600">{invited}</p>
          </CardContent>
        </Card>
      </div>

      {/* Questionnaire List */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Assigned Questionnaires
            </CardTitle>
            <CardDescription>
              All questionnaires assigned to this supplier with status and renewal tracking
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setAssignDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Assign Questionnaire
          </Button>
        </CardHeader>
        <CardContent>
          {questionnaires.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No questionnaires assigned to this supplier</p>
            </div>
          ) : (
            <div className="space-y-3">
              {questionnaires.map((q) => {
                const statusBadge = STATUS_BADGE[q.status] || STATUS_BADGE['invited'];

                return (
                  <div key={q.id} className={`flex items-center gap-4 p-4 border rounded-lg transition-colors ${
                    q.passed === false
                      ? 'bg-red-50 border-red-200 hover:bg-red-100 dark:bg-red-950/30 dark:border-red-800'
                      : 'hover:bg-muted/30'
                  }`}>
                    <div className={`p-2 rounded-full shrink-0 ${
                      q.status === 'approved' ? 'bg-green-100' :
                      q.status === 'submitted' ? 'bg-blue-100' :
                      q.status === 'expired' ? 'bg-red-100' :
                      'bg-gray-100'
                    }`}>
                      {q.status === 'approved' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : q.status === 'expired' ? (
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                      ) : q.status === 'invited' ? (
                        <Clock className="h-5 w-5 text-gray-600" />
                      ) : (
                        <FileText className="h-5 w-5 text-blue-600" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{q.template_name || 'Questionnaire'}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge className={`text-[10px] px-1.5 py-0 ${statusBadge.className}`}>
                          {statusBadge.label}
                        </Badge>
                      </div>
                    </div>

                    {q.score_percentage != null && q.score_percentage > 0 && (
                      <div className="text-right shrink-0">
                        <p className={`text-sm font-semibold ${
                          q.passed === false ? 'text-red-600' : q.passed === true ? 'text-green-600' : ''
                        }`}>{Math.round(q.score_percentage)}%</p>
                        <p className={`text-[10px] font-medium ${
                          q.passed === false
                            ? 'text-red-600'
                            : q.passed === true
                            ? 'text-green-600'
                            : 'text-muted-foreground'
                        }`}>
                          {q.passed ? 'Passed' : 'Failed'}
                        </p>
                      </div>
                    )}

                    <div className="text-right shrink-0 min-w-[100px]">
                      {q.submitted_at ? (
                        <p className="text-xs text-muted-foreground">
                          Submitted {formatDistanceToNow(new Date(q.submitted_at), { addSuffix: true })}
                        </p>
                      ) : q.invited_at ? (
                        <p className="text-xs text-muted-foreground">
                          Invited {formatDistanceToNow(new Date(q.invited_at), { addSuffix: true })}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(q.created_at), { addSuffix: true })}
                        </p>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/intelligence/qualifications/${q.id}`, { state: { returnTo: `/suppliers/${supplierId}?tab=questionnaires` } })}
                      className="shrink-0"
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      {q.status === 'submitted' || q.status === 'under_review' ? 'Review' : 'View'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AssignQuestionnaireDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        supplierId={supplierId}
        supplierName={supplierName || 'this supplier'}
        onAssigned={loadQuestionnaires}
      />
    </div>
  );
}
